import { useState } from "react";
import { Check, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskText, Eyebrow } from "@/components/motion";
import { MEDIA } from "@/lib/content";
import { copyText, pageUrl } from "@/lib/api";

const SPEAKERS = [
  { k: "valentina", init: "V", role: "Master Viso · co-fondatrice", name: "Valentina", bio: "Dal 2022 punto di riferimento dell'anti-age non invasivo in Italia. Ha creato i protocolli viso Skin Lovers." },
  { k: "renata", init: "R", role: "Master Corpo", name: "Renata", bio: "Guida i protocolli corpo Skin Lovers e la formazione dei centri." },
];

export const Speakers = () => (
  <section className="section border-t border-line" data-testid="speakers-section">
    <div className="wrap">
      <Eyebrow>Le relatrici</Eyebrow>
      <MaskText className="h2 mt-6" lines={["Chi ti guida", <em className="gold-text">nelle 2 serate</em>]} />
      <div className="mt-14 grid gap-5 md:grid-cols-2">
        {SPEAKERS.map((s, i) => (
          <Reveal key={s.k} delay={i * 0.12} className="h-full">
            <article className="card-lux group grid h-full grid-cols-[120px_1fr] gap-6 p-5 sm:grid-cols-[180px_1fr] sm:p-6" data-testid={`speaker-${s.k}`}>
              <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[radial-gradient(90%_70%_at_50%_25%,rgba(199,154,75,0.3),#120e08_70%)]">
                {MEDIA.speakers[s.k] ? (
                  <img src={MEDIA.speakers[s.k]} alt={s.name} loading="lazy" className="h-full w-full object-cover grayscale-[30%] transition duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                ) : (
                  <span className="absolute inset-0 flex items-center justify-center font-serif text-7xl text-gold-light/80">{s.init}</span>
                )}
              </div>
              <div className="flex flex-col justify-center">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">{s.role}</div>
                <h3 className="mt-2 text-4xl text-cream">{s.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sand">{s.bio}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const YES = ["Sei la titolare di un centro estetico o di un istituto di bellezza", "Vuoi offrire un Natural Anti-Age vero, senza aghi e senza eccessi", "Vuoi che le clienti ti scelgano per i risultati, non per il prezzo", "Vuoi alzare il listino con trattamenti che lo giustificano", "Vuoi proporre percorsi e non singole sedute", "Cerchi un metodo completo, con prodotti, protocolli e formazione continua", "Stai pensando di far fare al tuo centro un salto di livello nei prossimi mesi"];
const NO = ["Sei una dipendente o una collaboratrice (puoi però girare questa pagina alla tua titolare)", "Lavori a domicilio senza un centro", "Cerchi una ricetta gratis da copiare il giorno dopo", "Pensi che l'anti-age sia solo questione di macchinari", "Competi solo sul prezzo e vuoi continuare così", "Non vuoi investire nella tua formazione e in quella del tuo staff"];

const List = ({ title, items, yes }) => (
  <div className="card-lux h-full p-7 sm:p-9">
    <h3 className={`text-3xl ${yes ? "text-gold-light" : "text-cream"}`}>{title}</h3>
    <ul className="mt-6 space-y-4">
      {items.map((it) => (
        <li key={it} className="flex gap-3 text-[15px] leading-relaxed text-sand">
          {yes ? <Check className="mt-1 h-4 w-4 shrink-0 text-gold" /> : <X className="mt-1 h-4 w-4 shrink-0 text-[#c98a70]" />}
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const CopyLinkButton = ({ testid }) => {
  const [done, setDone] = useState(false);
  const onClick = async () => {
    const ok = await copyText(pageUrl());
    setDone(ok);
    if (ok) toast.success("Link copiato. Incollalo in un messaggio alla tua titolare.");
    else toast.error("Non sono riuscita a copiare il link. Copialo dalla barra dell'indirizzo.");
  };
  return (
    <button type="button" onClick={onClick} className="btn-ghost" data-testid={testid}>
      {done ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      {done ? "Link copiato" : "Copia il link per la titolare"}
    </button>
  );
};

export const ForWho = () => (
  <section className="section border-t border-line bg-ink2" data-testid="forwho-section">
    <div className="wrap">
      <Eyebrow>Per chi è</Eyebrow>
      <MaskText className="h2 mt-6" lines={["Un workshop riservato", <em className="gold-text">alle titolari</em>]} />
      <Reveal><p className="lead mt-8 max-w-3xl">Parliamo di come cresce un centro: posizionamento, prezzi, fatturato e la possibilità di diventare Centro Official Partner. Sono decisioni che prende la titolare, per questo l'accesso è riservato solo a lei.</p></Reveal>
      <div className="mt-14 grid gap-5 md:grid-cols-2">
        <Reveal className="h-full"><List title="È per te se" items={YES} yes /></Reveal>
        <Reveal delay={0.1} className="h-full"><List title="Non è per te se" items={NO} /></Reveal>
      </div>
      <Reveal>
        <div className="mt-5 flex flex-col gap-6 rounded-2xl border border-gold/30 bg-gold/[0.06] p-7 sm:p-9 md:flex-row md:items-center md:justify-between" data-testid="not-owner-box">
          <div>
            <h3 className="text-3xl text-cream">Non sei la titolare?</h3>
            <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-sand">Questo workshop può cambiare il tuo centro. Inoltra la pagina alla tua titolare: basta un messaggio.</p>
          </div>
          <CopyLinkButton testid="forward-copy-link-btn" />
        </div>
      </Reveal>
    </div>
  </section>
);
